import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Loader2, RefreshCw, FileSpreadsheet, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { CnCaseDrawer } from './CnCaseDrawer';
import { CnByTrainer } from './CnByTrainer';
import { CnKpis } from './CnKpis';

export const inr = n => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
export const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
export const STAGE = {
  awaiting_trainer: ['Awaiting trainer', 'bg-amber-50 text-amber-700'], awaiting_review: ['Awaiting review', 'bg-blue-50 text-blue-700'],
  closed: ['Closed', 'bg-emerald-50 text-emerald-700'], unlinked: ['Trainer not linked', 'bg-rose-50 text-rose-700'], not_training: ['Not training concern', 'bg-slate-100 text-slate-500'],
};
const sel = 'text-xs border border-slate-200 rounded-md px-2 h-8 bg-white';

const download = async (path, name) => {
  try {
    const r = await api.get(path, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch { toast.error('Export failed'); }
};

export default function CnReviewPage() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('training');
  const [f, setF] = useState({ q: '', type: '', trainer: '', stage: '', flag: '', from: '', to: '' });
  const [open, setOpen] = useState(null);
  const poll = useRef(null);
  const load = useCallback(() => api.get('/cn-review/cases').then(r => { setD(r.data); return r.data; }).catch(e => toast.error(e.response?.data?.detail || 'Could not load')), []);
  useEffect(() => { load(); return () => clearInterval(poll.current); }, [load]);

  const sync = async () => {
    await api.post('/cn-review/sync');
    setD(v => ({ ...v, state: { ...v.state, running: true } }));
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const s = (await api.get('/cn-review/state')).data;
      if (!s.running) { clearInterval(poll.current); await load(); s.last_error ? toast.error(s.last_error) : toast.success('Synced from Zoho Books'); }
    }, 3000);
  };
  const rows = useMemo(() => !d ? [] : d.cases.filter(c => (tab === 'all' || c.is_training)
    && (!f.type || c.cn_type === f.type) && (!f.trainer || (c.trainer_name || c.cn_trainer_name) === f.trainer)
    && (!f.stage || c.stage === f.stage) && (!f.flag || (f.flag === 'yes' ? c.flags?.length : !c.flags?.length))
    && (!f.from || c.date >= f.from) && (!f.to || c.date <= f.to)
    && (!f.q || `${c.creditnote_number} ${c.customer_name} ${c.enrollment_no} ${c.refund_reason}`.toLowerCase().includes(f.q.toLowerCase()))), [d, f, tab]);

  if (!d) return <div className="py-20 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  const types = [...new Set(d.cases.map(c => c.cn_type).filter(Boolean))].sort();
  const trainers = [...new Set(d.cases.map(c => c.trainer_name || c.cn_trainer_name).filter(Boolean))].sort();
  const openCase = open && d.cases.find(c => c.creditnote_id === open);

  return (
    <div className="space-y-5" data-testid="cn-review-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Credit Note Review</h1>
          <p className="text-sm text-slate-500 mt-0.5">Zoho Books credit notes from {d.settings.start_date} · "Trainer / Training Concern" cases go to the trainer named on the CN · syncs nightly 11:59 PM IST</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={sync} disabled={d.state.running} data-testid="cn-sync-btn">
            {d.state.running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}{d.state.running ? 'Syncing…' : 'Sync now'}</Button>
          <Button size="sm" variant="outline" onClick={() => download(`/cn-review/export.xlsx?scope=${tab}`, 'credit-note-review.xlsx')} data-testid="cn-export-xlsx-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Excel</Button>
          <Button size="sm" variant="outline" onClick={() => download('/cn-review/export.pdf', 'credit-note-review.pdf')} data-testid="cn-export-pdf-btn"><FileText className="w-3.5 h-3.5 mr-1.5" />PDF</Button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 -mt-3" data-testid="cn-synced-at">{d.state.synced_at ? `Last synced ${when(d.state.synced_at)} · ${d.state.synced_by}` : 'Not synced yet'}{d.state.last_error && <span className="text-rose-600"> · last sync failed: {d.state.last_error}</span>}</p>
      <CnKpis t={d.totals} onPick={stage => { setTab('training'); setF(v => ({ ...v, stage: stage === 'flagged' ? '' : stage, flag: stage === 'flagged' ? 'yes' : '' })); }} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="training" data-testid="cn-tab-training">Training Concern cases ({d.totals.training})</TabsTrigger>
          <TabsTrigger value="all" data-testid="cn-tab-all">All credit notes ({d.totals.all})</TabsTrigger>
          <TabsTrigger value="trainers" data-testid="cn-tab-trainers">By trainer</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === 'trainers' ? <CnByTrainer rows={d.by_trainer} /> : (
        <Card className="p-4" data-testid="cn-table">
          <div className="flex flex-wrap gap-2 mb-3">
            <Input value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="Search CN, student, enrolment, reason" className="h-8 w-64 text-xs" data-testid="cn-filter-search" />
            {tab === 'all' && <select className={sel} value={f.type} onChange={e => setF({ ...f, type: e.target.value })} data-testid="cn-filter-type"><option value="">All types</option>{types.map(t => <option key={t}>{t}</option>)}</select>}
            <select className={sel} value={f.trainer} onChange={e => setF({ ...f, trainer: e.target.value })} data-testid="cn-filter-trainer"><option value="">All trainers</option>{trainers.map(t => <option key={t}>{t}</option>)}</select>
            <select className={sel} value={f.stage} onChange={e => setF({ ...f, stage: e.target.value })} data-testid="cn-filter-stage"><option value="">All stages</option>{Object.entries(STAGE).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}</select>
            <select className={sel} value={f.flag} onChange={e => setF({ ...f, flag: e.target.value })} data-testid="cn-filter-flag"><option value="">Flag: any</option><option value="yes">Flagged</option><option value="no">Not flagged</option></select>
            <Input type="date" value={f.from} onChange={e => setF({ ...f, from: e.target.value })} className="h-8 w-36 text-xs" data-testid="cn-filter-from" />
            <Input type="date" value={f.to} onChange={e => setF({ ...f, to: e.target.value })} className="h-8 w-36 text-xs" data-testid="cn-filter-to" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr>
                <th className="text-left py-1">Date</th><th className="text-left">CN</th><th className="text-left">Student</th><th className="text-left">Enrol</th><th className="text-right">Amount</th>
                <th className="text-left pl-3">Type</th><th className="text-left">Trainer on CN</th><th className="text-left">Batch trainer</th><th className="text-left">Created by</th>
                <th className="text-left">Trainer status</th><th className="text-left">Stage</th></tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">No credit notes match.</td></tr>}
                {rows.map(c => (
                  <tr key={c.creditnote_id} onClick={() => setOpen(c.creditnote_id)} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" data-testid={`cn-row-${c.creditnote_id}`}>
                    <td className="py-2 whitespace-nowrap">{c.date}</td><td className="font-mono whitespace-nowrap">{c.creditnote_number}</td>
                    <td>{c.customer_name}</td><td>{c.enrollment_no || '—'}</td><td className="text-right font-semibold">{inr(c.total)}</td>
                    <td className="pl-3">{c.cn_type || '—'}</td>
                    <td>{c.cn_trainer_name || '—'}{c.trainer_name && c.trainer_name !== c.cn_trainer_name && <span className="text-slate-400"> → {c.trainer_name}</span>}</td>
                    <td>{c.batch_trainers?.join(', ') || '—'}{c.flags?.length > 0 && <AlertTriangle className="w-3.5 h-3.5 inline ml-1 text-rose-600" data-testid={`cn-flag-${c.creditnote_id}`} />}</td>
                    <td className="text-slate-500">{c.created_by_name}</td><td>{c.trainer_status_label || '—'}</td>
                    <td><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${STAGE[c.stage][1]}`}>{c.outcome_label || STAGE[c.stage][0]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <CnCaseDrawer c={openCase} trainers={d.trainers} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
